<template>
  <div class="auth-page">
    <!-- Background Video -->
    <div class="auth-background" :class="{ 'fallback-active': videoFailed }">
      <video
        v-if="!videoFailed"
        autoplay
        muted
        loop
        playsinline
        class="background-video"
        @error="onVideoError"
      >
  <source :src="videoSource" type="video/mp4">
      </video>
      <div class="video-overlay"></div>
    </div>

    <!-- Main Content -->
    <div class="auth-content">
      <v-container fluid class="auth-container">
        <v-row align="center" justify="center" class="min-height-screen">
          <v-col cols="12" sm="8" md="6" lg="4" xl="3">
            <!-- Language Switcher (Hidden on mobile - use header switcher) -->
            <div class="text-end mb-4 d-none d-sm-block">
              <LanguageSwitcher />
            </div>

            <!-- Auth Card -->
            <v-card 
              class="auth-card glass-effect"
              elevation="24"
            >
              <!-- Header -->
              <v-card-title class="auth-header text-center pa-6">
                <div class="auth-logo mb-4">
                  <v-img
                    :src="logoSrc"
                    alt="Көркем Авто"
                    max-width="80"
                    class="mx-auto"
                  />
                </div>
                <h2 class="text-h4 text-gradient">
                  {{ $t('auth.forgotPassword') }}
                </h2>
                <p class="text-subtitle-1 mt-2 text-medium-emphasis">
                  Құпия сөзді қалпына келтіру
                </p>
              </v-card-title>

              <!-- Form -->
              <v-card-text class="pa-6 pt-0">
                <div v-if="!codeSent">
                  <!-- Phone Input Form -->
                  <v-form 
                    ref="phoneForm"
                    v-model="phoneFormValid"
                    @submit.prevent="requestResetCode"
                  >
                    <p class="text-body-1 mb-6 text-medium-emphasis">
                      Құпия сөзді қалпына келтіру үшін телефон нөміріңізді енгізіңіз.
                      Біздің маман сізге жаңа код жібереді.
                    </p>

                    <!-- Phone Input -->
                    <PhoneInput
                      v-model="resetData.phone"
                      :label="$t('auth.phone')"
                      :rules="phoneRules"
                      class="mb-4"
                    />

                    <!-- Error Alert -->
                    <v-alert
                      v-if="error"
                      type="error"
                      variant="tonal"
                      class="mb-4"
                      closable
                      @click:close="clearError"
                    >
                      {{ error }}
                    </v-alert>

                    <!-- Submit Button -->
                    <v-btn
                      type="submit"
                      block
                      size="large"
                      class="btn-primary mb-4"
                      :loading="loading"
                      :disabled="!phoneFormValid"
                    >
                      <v-icon start>mdi-send</v-icon>
                      Код сұрау
                    </v-btn>
                  </v-form>
                </div>

                <div v-else>
                  <!-- Reset Code Form -->
                  <v-form 
                    ref="resetForm"
                    v-model="resetFormValid"
                    @submit.prevent="resetPassword"
                  >
                    <v-alert
                      type="success"
                      variant="tonal"
                      class="mb-6"
                    >
                      <div class="text-subtitle-2 mb-2">
                        Код сұранысы жіберілді!
                      </div>
                      <div class="text-body-2">
                        Біздің маман сізге жақын уақытта хабарласып, 
                        қалпына келтіру кодын жібереді.
                      </div>
                      <div class="text-body-2 mt-2">
                        <strong>Телефон:</strong> {{ resetData.phone }}
                      </div>
                    </v-alert>

                    <!-- Reset Code Input -->
                    <v-text-field
                      v-model="resetData.resetCode"
                      label="Қалпына келтіру коды"
                      :rules="codeRules"
                      variant="outlined"
                      prepend-inner-icon="mdi-key"
                      placeholder="123456"
                      class="mb-4"
                      clearable
                    />

                    <!-- New Password -->
                    <v-text-field
                      v-model="resetData.newPassword"
                      :label="$t('auth.newPassword')"
                      :rules="passwordRules"
                      :type="showPassword ? 'text' : 'password'"
                      variant="outlined"
                      prepend-inner-icon="mdi-lock"
                      :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      class="mb-4"
                      @click:append-inner="showPassword = !showPassword"
                    />

                    <!-- Confirm Password -->
                    <v-text-field
                      v-model="resetData.confirmPassword"
                      :label="$t('auth.confirmPassword')"
                      :rules="confirmPasswordRules"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      variant="outlined"
                      prepend-inner-icon="mdi-lock-check"
                      :append-inner-icon="showConfirmPassword ? 'mdi-eye-off' : 'mdi-eye'"
                      class="mb-4"
                      @click:append-inner="showConfirmPassword = !showConfirmPassword"
                    />

                    <!-- Error Alert -->
                    <v-alert
                      v-if="error"
                      type="error"
                      variant="tonal"
                      class="mb-4"
                      closable
                      @click:close="clearError"
                    >
                      {{ error }}
                    </v-alert>

                    <!-- Submit Button -->
                    <v-btn
                      type="submit"
                      block
                      size="large"
                      class="btn-primary mb-4"
                      :loading="loading"
                      :disabled="!resetFormValid"
                    >
                      <v-icon start>mdi-check</v-icon>
                      Құпия сөзді өзгерту
                    </v-btn>

                    <!-- Request New Code -->
                    <v-btn
                      variant="text"
                      color="primary"
                      block
                      size="small"
                      @click="requestNewCode"
                    >
                      <v-icon start>mdi-refresh</v-icon>
                      Жаңа код сұрау
                    </v-btn>
                  </v-form>
                </div>

                <!-- Navigation Links -->
                <v-divider class="my-6">
                  <span class="text-medium-emphasis px-4">немесе</span>
                </v-divider>

                <div class="text-center">
                  <v-btn
                    variant="text"
                    color="primary"
                    @click="$router.push('/login')"
                  >
                    <v-icon start>mdi-arrow-left</v-icon>
                    {{ $t('auth.loginHere') }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>

            <!-- Footer Links -->
            <div class="auth-footer text-center mt-6">
              <v-btn
                variant="text"
                size="small"
                @click="$router.push('/')"
              >
                <v-icon start>mdi-home</v-icon>
                {{ $t('nav.home') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue'
import PhoneInput from '@/components/ui/PhoneInput.vue'
import { withBasePath } from '@/utils/path'

// Composables
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()

// Refs
const phoneForm = ref(null)
const resetForm = ref(null)
const phoneFormValid = ref(false)
const resetFormValid = ref(false)
const codeSent = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const resetData = ref({
  phone: '',
  resetCode: '',
  newPassword: '',
  confirmPassword: ''
})

// Background video fallback handling
const videoFailed = ref(false)
const onVideoError = () => {
  videoFailed.value = true
}

const videoSource = withBasePath('/videos/hero-video-3.mp4')
const logoSrc = withBasePath('/logo.svg')

// Computed
const loading = computed(() => authStore.loading)
const error = computed(() => authStore.error)

// Validation rules
const phoneRules = [
  v => !!v || t('validation.required'),
  v => {
    const digits = (v || '').replace(/\D/g, '')
    // Строго требуем +7 и ровно 11 цифр начиная с 7
    return (digits.length === 11 && digits.startsWith('7') && v.startsWith('+7')) || t('validation.phone')
  }
]

const codeRules = [
  v => !!v || t('validation.required'),
  v => /^\d{6}$/.test(v) || 'Код 6 саннан тұруы керек'
]

const passwordRules = [
  v => !!v || t('validation.required'),
  v => v.length >= 6 || t('validation.minLength', { min: 6 }),
  v => v.length <= 50 || t('validation.maxLength', { max: 50 })
]

const confirmPasswordRules = [
  v => !!v || t('validation.required'),
  v => v === resetData.value.newPassword || t('validation.passwordMatch')
]

// Methods
const requestResetCode = async () => {
  // Console log removed
  if (!phoneFormValid.value) {
    return
  }

  try {
    // Normalize phone number before sending - extract only digits and ensure starts with 7
    const originalPhone = resetData.value.phone
    let normalizedPhone = originalPhone.replace(/\D/g, '') // Remove all non-digits
    
    // Convert 8XXXXXXXXXX to 7XXXXXXXXXX
    if (normalizedPhone.startsWith('8') && normalizedPhone.length === 11) {
      normalizedPhone = '7' + normalizedPhone.slice(1)
    }
    
    // Ensure it starts with 7 and has 11 digits
    if (!normalizedPhone.startsWith('7') || normalizedPhone.length !== 11) {
      // Console log removed
      throw new Error('Телефон нөмірі дұрыс емес')
    }
    
    // Add + prefix for backend consistency
    const phoneWithPrefix = `+${normalizedPhone}`
    
    await authStore.forgotPassword(phoneWithPrefix)
    codeSent.value = true
    
    // Additional success logging for mobile
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      // Console log removed
    }
    
  } catch (err) {
    // Console log removed
    // Console log removed
    // Console log removed
    // Console log removed
    
    // Additional mobile debugging
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      // Console log removed
    }
  }
}

const resetPassword = async () => {
  // Console log removed
  // Console log removed)
  // Console log removed
  // Console log removed

  if (!resetFormValid.value) {
    // Console log removed
    return
  }

  try {
    // Normalize phone number before sending - same logic as requestResetCode
    const originalPhone = resetData.value.phone
    let normalizedPhone = originalPhone.replace(/\D/g, '')
    
    // Console log removed
    // Console log removed
    
    if (normalizedPhone.startsWith('8') && normalizedPhone.length === 11) {
      normalizedPhone = '7' + normalizedPhone.slice(1)
      // Console log removed
    }
    
    if (!normalizedPhone.startsWith('7') || normalizedPhone.length !== 11) {
      // Console log removed
      throw new Error('Телефон нөмірі дұрыс емес')
    }
    
    // Add + prefix for backend consistency
    const phoneWithPrefix = `+${normalizedPhone}`
    
    const resetPayload = {
      phone: phoneWithPrefix,
      newPassword: resetData.value.newPassword,
      resetCode: resetData.value.resetCode
    }
    
    // Console log removed
    
    await authStore.changePassword(resetPayload)
    
    // Console log removed
    
    // Additional success logging for mobile
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      // Console log removed
    }
    
    // Redirect to login
    // Console log removed
    router.push({
      path: '/login',
      query: { 
        message: 'password_reset_success',
        phone: resetData.value.phone 
      }
    })
    
  } catch (err) {
    // Console log removed
    // Console log removed
    // Console log removed
    // Console log removed
    
    // Additional mobile debugging
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      // Console log removed
    }
  }
}

const requestNewCode = async () => {
  codeSent.value = false
  resetData.value.resetCode = ''
  resetData.value.newPassword = ''
  resetData.value.confirmPassword = ''
  
  await requestResetCode()
}

const clearError = () => {
  authStore.clearError()
}

// Mobile device detection and debugging
const checkMobileDevice = () => {
  const isMobile = /Mobi|Android/i.test(navigator.userAgent)
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  
  // Console log removed
  
  return { isMobile, isIOS }
}

// Initialize mobile debugging on component mount
import { onMounted } from 'vue'
onMounted(() => {
  checkMobileDevice()
  // Console log removed
})
</script>

<style scoped>
.auth-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
}

.auth-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

.background-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
}

/* Fallback: static gradient background when video fails */
.auth-background.fallback-active {
  background: linear-gradient(135deg, #0d47a1 0%, #1976d2 50%, #42a5f5 100%);
}

.auth-content {
  position: relative;
  z-index: 1;
  width: 100%;
}

.auth-container {
  min-height: 100vh;
  padding: 2rem 1rem;
}

.min-height-screen {
  min-height: 100vh;
}

.auth-card {
  border-radius: 24px;
  border: 1px solid rgba(255, 193, 7, 0.3);
  animation: slideInUp 0.6s ease-out;
}

.auth-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .auth-container {
    padding: 1rem 0.5rem;
  }
  
  .auth-card {
    margin: 0 0.5rem;
  }
}

@media (max-width: 480px) {
  .text-h4 {
    font-size: 1.75rem !important;
  }
  
  .auth-logo img {
    max-width: 60px;
  }
}
</style>